<template>
  <ModalDialog :title="t('planCancelTitle')" :open="open" without-overflow @close="closeModal">
    <div class="w-[38rem]">
      <PlanCancellationReasons
        v-if="step === 'reasons'"
        v-model:selected-reason="selectedReason"
        v-model:reason-comment="selectedReasonComment"
        :loading-continue="loadingDashboard"
        @continue="goToSummary"
        @close="closeModal"
      />
      <PlanCancellationSummary
        v-else
        :plan="plan"
        :reason="selectedReason"
        :comment="selectedReasonComment"
        :dashboard-data="dashboardData"
        :is-gmv-blocked="isGmvBlocked"
        @scheduleCancelPlan="scheduleCancelPlan"
        @immediateCancelPlan="immediateCancelPlan"
        @contactIntercom="openIntercomWithContext"
        @close="closeModal"
      />
    </div>
  </ModalDialog>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useDashboard } from "@/vue/api/modules/dashboard";

import type { Reason } from "./planCancellation.types";
import type { CountryCode } from "@/vue/types/countries";

//Components
import ModalDialog from "@molecules/ModalDialog.vue";
import PlanCancellationReasons from "./components/PlanCancellationReasons.vue";
import PlanCancellationSummary from "./components/PlanCancellationSummary.vue";

// Utils
import { startOfDay, subDays, endOfDay, differenceInHours } from "date-fns";

// Application
import { useDashboardApplication, useLocalStorageApp } from "@application";

// Stores
import { storeToRefs } from "pinia";
import { useAccountStore, useSessionStore } from "@store";

// Helpers
import Intercom from "@helpers/intercom";

// I18n
import { useI18n } from "vue-i18n";

// Services
import { usePlan } from "@/vue/api/modules/plan/plan";

// Domain
import { Plan } from "@domain/plan";
import type { IDashboardDataSet } from "@domain/dashboard/base";
import { DashboardData } from "@domain/dashboard/dashboard";

const { t, n } = useI18n();
const sn = (value: number | undefined, format: string): string => n(isFinite(value!) && value ? value : 0, format);

const dashboardApp = useDashboardApplication();
const localStorageApp = useLocalStorageApp();
const dashboardAPI = useDashboard();

const accountStore = useAccountStore();
const sessionStore = useSessionStore();
const { session } = storeToRefs(sessionStore);
const isMasterUser = session.value?.isMasterUser;

const GMV_THRESHOLDS: Partial<Record<CountryCode, number>> = {
  ar: 15_000_000, // 15_000_000
  br: 60_000, // 60_000
  mx: 100_000_000, // 100_000_000
};

const planAPI = usePlan();

//Props
const props = withDefaults(
  defineProps<{
    plan: Plan;
  }>(),
  {},
);

const emit = defineEmits<{
  (e: "planCanceled"): void;
}>();

const open = ref(false);
const resetValues = () => {
  step.value = "reasons";
  selectedReason.value = getDefaultReason();
  selectedReasonComment.value = "";
};

const openModal = () => {
  resetValues();
  open.value = true;
};
const closeModal = () => {
  open.value = false;
};

defineExpose({
  openModal,
  closeModal,
});

const getDefaultReason = (): Reason => {
  return {
    key: "empty",
    value: "‎",
    data: {
      showComment: false,
      showContactSupport: false,
      showWarning: false,
      validOption: false,
    },
  };
};
const selectedReason = ref<Reason>(getDefaultReason());
const selectedReasonComment = ref<string>("");

const step = ref<"reasons" | "summary">("reasons");

const isGmvBlocked = computed(() => {
  if (isMasterUser) return false;
  const country = session.value?.account.country;
  const threshold = country ? GMV_THRESHOLDS[country] : undefined;
  if (threshold == null) return false;
  const renewalInHours = differenceInHours(new Date(props.plan.periodEnds), new Date());
  if (renewalInHours < 72) return false;
  return (dashboardData.value?.store.total.current.amount ?? 0) > threshold;
});

const goToSummary = async () => {
  if (dashboardFetchPromise) await dashboardFetchPromise;
  step.value = "summary";
};

const openIntercomWithContext = () => {
  const reasonLabel = selectedReason.value.value;
  const comment = selectedReasonComment.value;
  const message = comment
    ? t("gmvIntercomMessage", { reason: reasonLabel, comment })
    : t("gmvIntercomMessageNoComment", { reason: reasonLabel });
  Intercom.showNewMessage(message);
  closeModal();
};

const scheduleCancelPlan = async () => {
  await planAPI.scheduleCancelPlan({
    reason: selectedReason.value.key,
    comments: selectedReasonComment.value !== "" ? selectedReasonComment.value : undefined,
    executionDate: subDays(new Date(props.plan.periodEnds), 1).toISOString(),
    plan: props.plan,
    assistedSales: {
      count: dashboardData.value?.store.assisted.current.count || 0,
      amount: sn(dashboardData.value?.store.assisted.current.amount || 0, "currency"),
    },
  });
  closeModal();
  emit("planCanceled");
};
const immediateCancelPlan = async () => {
  await planAPI.immediateCancelPlan({
    reason: selectedReason.value.key,
    comments: selectedReasonComment.value !== "" ? selectedReasonComment.value : undefined,
  });
  closeModal();
  emit("planCanceled");
};

const dashboardData = ref<DashboardData>();
const loadingDashboard = ref(false);
let dashboardFetchPromise: Promise<void> | null = null;

const lastDay = endOfDay(subDays(new Date(), 1));
const calcFrom = (days: number) => startOfDay(subDays(lastDay, days - 1));

const isDashboardCacheValid = (): boolean => {
  const ts = localStorageApp.get<number>({ id: "dashboardDataSavedAt" });
  if (!ts) return false;
  const savedDate = new Date(ts);
  const today = new Date();
  return (
    savedDate.getFullYear() === today.getFullYear() &&
    savedDate.getMonth() === today.getMonth() &&
    savedDate.getDate() === today.getDate()
  );
};

const buildDashboardData = (raw: IDashboardDataSet) => {
  dashboardData.value = dashboardApp.calculateDashboardData({
    data: raw,
    periodFrom: calcFrom(30),
    periodTo: lastDay,
    planType: props.plan.type,
    planCost: accountStore.getConfig()?.monthly_cost || props.plan.price.total,
    batchType: "all",
    attributionModel: accountStore.getConfig()?.attribution_model ?? "opens",
  });
};

const DASHBOARD_FETCH_TIMEOUT_MS = 5_000;

const loadDashboardData = async () => {
  const cached = localStorageApp.get<IDashboardDataSet>({ id: "dashboardData" });
  if (cached && isDashboardCacheValid()) {
    buildDashboardData(cached);
    return;
  }

  loadingDashboard.value = true;

  const timeout = new Promise<void>((resolve) =>
    setTimeout(() => {
      loadingDashboard.value = false;
      resolve();
    }, DASHBOARD_FETCH_TIMEOUT_MS),
  );

  const fetch = (async () => {
    try {
      const raw = await dashboardAPI.get(calcFrom(30), lastDay);
      if (raw) {
        localStorageApp.save({ id: "dashboardData", value: raw });
        localStorageApp.save({ id: "dashboardDataSavedAt", value: Date.now() });
        buildDashboardData(raw);
      }
    } catch {
      if (cached) buildDashboardData(cached);
    } finally {
      loadingDashboard.value = false;
    }
  })();

  await Promise.race([fetch, timeout]);
};

onMounted(() => {
  dashboardFetchPromise = loadDashboardData();
});
</script>

<i18n lang="jsonc">
{
  "es": {
    "planCancelTitle": "Cancelar suscripción",
    "gmvIntercomMessage": "Hola, quisiera dar de baja mi plan. Motivo: {reason}. Comentario: {comment}",
    "gmvIntercomMessageNoComment": "Hola, quisiera dar de baja mi plan. Motivo: {reason}",
  },
  "pt": {
    "planCancelTitle": "Cancelar assinatura",
    "gmvIntercomMessage": "Olá, gostaria de cancelar meu plano. Motivo: {reason}. Comentário: {comment}",
    "gmvIntercomMessageNoComment": "Olá, gostaria de cancelar meu plano. Motivo: {reason}",
  },
}
</i18n>
